import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Constants from '../../submodules/logictry_config/constants';
import Navigation from '../../services/Navigation';
import convertUrlfromS3toCloudFront from '../../utils/convertUrlfromS3toCloudFront';
import escapeCssUrl from '../../utils/escapeCssUrl';
import { PremiumIcon, LockIcon } from '../../styledhtml/Icon';
import {
  RAIL_CARD_WIDTH,
  RAIL_CARD_WIDTH_MOBILE,
  RAIL_CARD_WIDTH_TABLET,
  fallbackGradient,
  gradientFromColor,
} from '../../styledhtml/LogicBaseCard';

const Card = styled.article`
  width: ${RAIL_CARD_WIDTH_MOBILE};
  aspect-ratio: 16 / 9;
  flex-shrink: 0;
  cursor: pointer;
  box-sizing: border-box;
  border-radius: 12px;
  border: none;
  overflow: hidden;
  position: relative;
  min-width: 0;
  ${Constants.MediaTablet} {
    width: ${RAIL_CARD_WIDTH_TABLET};
  }
  ${Constants.MediaDesktop} {
    width: ${RAIL_CARD_WIDTH};
  }
`;

const Bg = styled.div`
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-color: ${Constants.PrimaryColor};
`;

const Fade = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.78) 0%, rgba(0, 0, 0, 0.22) 55%, rgba(0, 0, 0, 0.06) 100%);
`;

const Body = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 1.15rem 1.2rem 1.25rem;
  color: white;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  min-width: 0;
`;

/** Title + badges inline (same pattern as LogicBaseCollectionPage title row). */
const TitleWithIcons = styled.div`
  display: flex;
  min-width: 0;
  flex: 0 1 auto;
  max-width: 100%;
`;

const Title = styled.h3`
  margin: 0;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  font-size: 1.3rem;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.02em;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.35);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** Crown/lock assets are dark; invert so badges read on the card's dark gradient overlay. */
const RAIL_OVERLAY_ICON_STYLE = { flexShrink: 0, filter: 'invert(1)' };

/** Match collection page: marginTop 0; slight translateY on smaller rail type (~1.05rem). */
const RAIL_LOCK_ICON_STYLE = {
  height: 14,
  width: 14,
  marginLeft: 8,
  marginTop: 0,
  transform: 'translateY(-3px)',
  opacity: 0.95,
  ...RAIL_OVERLAY_ICON_STYLE,
};
const RAIL_PREMIUM_ICON_STYLE = {
  height: 16,
  width: 16,
  marginLeft: 8,
  marginTop: 0,
  transform: 'translateY(-4px)',
  opacity: 0.95,
  ...RAIL_OVERLAY_ICON_STYLE,
};

/** Accepts both hydrated collection cache docs and lean Discover rows (`t`/`d`/`i`/`c`). */
function resolveCover(collection) {
  const img = collection?.image ?? collection?.i;
  if (!img) return null;
  const url = typeof img === 'string' ? img : img.url || img.src;
  if (!url) return null;
  return convertUrlfromS3toCloudFront(url, 720, 405);
}

function resolveLabel(collection) {
  if (!collection) return { title: '', color: undefined };
  return {
    title: collection.title ?? collection.t ?? '',
    color: collection.color ?? collection.c,
  };
}

/** Matches LogicBaseSearchBar / cache shapes: full `premiumCollection` or lean `pc` object. */
function collectionIsPremium(collection) {
  return !!(collection?.premiumCollection || (collection?.pc && typeof collection.pc === 'object'));
}

function collectionHasPassword(collection) {
  return !!(collection?.password || (collection?.pw != null && String(collection.pw).trim() !== ''));
}

CollectionRailCard.propTypes = {
  collection: PropTypes.object.isRequired,
  titleExtras: PropTypes.node,
  onClick: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object,
  /** Optional width override (CSS length). Ignored when `fullWidth`. */
  railWidth: PropTypes.string,
  /** Vertical stack (e.g. mobile): span container width */
  fullWidth: PropTypes.bool,
};

export default function CollectionRailCard({
  collection,
  titleExtras,
  onClick,
  className,
  style,
  fullWidth,
  railWidth,
}) {
  const { title, color } = resolveLabel(collection);
  const showPremiumBadge = collectionIsPremium(collection);
  const showLockBadge = collectionHasPassword(collection);
  const cover = resolveCover(collection);
  const bgStyle = cover
    ? { backgroundImage: `url("${escapeCssUrl(cover)}")` }
    : { background: color ? gradientFromColor(color) : fallbackGradient(collection?._id || title) };
  const go = onClick || (() => Navigation.push(`${Navigation.collections}/${collection._id}`));
  const cardStyle = fullWidth
    ? { width: '100%', maxWidth: '100%', ...style }
    : (railWidth ? { width: railWidth, ...style } : style);

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={go}
      onKeyDown={(e) => e.key === 'Enter' && go()}
      className={className}
      style={cardStyle}
    >
      <Bg style={bgStyle} />
      <Fade />
      <Body>
        <TitleRow>
          <TitleWithIcons>
            <Title>{title}</Title>
            {showLockBadge ? <LockIcon style={RAIL_LOCK_ICON_STYLE} aria-hidden /> : null}
            {showPremiumBadge ? <PremiumIcon style={RAIL_PREMIUM_ICON_STYLE} aria-hidden /> : null}
          </TitleWithIcons>
          {titleExtras}
        </TitleRow>
      </Body>
    </Card>
  );
}
